import { type FC, useCallback } from "react";

import { type ApolloError } from "@apollo/client";
import { List } from "@mantine/core";
import { Control, Modal } from "@snapchat/snap-design-system";
import { CheckIcon } from "@snapchat/snap-design-system-icons";

import { type ChangedField } from "../../state/lensImmerState";

import { error as errorClass } from "./SaveChangesModal.module.css";

interface SaveChangesModalProps {
    opened: boolean;
    onClose: () => void;
    onSave: () => Promise<void>;
    changedFields: ChangedField[];
    loading: boolean;
    updateError: ApolloError | undefined;
}

export const SaveChangesModal: FC<SaveChangesModalProps> = ({
    opened,
    onClose,
    onSave,
    changedFields,
    loading,
    updateError,
}) => {
    const handleSave = useCallback(async () => {
        await onSave();
    }, [onSave]);

    const handleCancel = useCallback(() => {
        onClose();
    }, [onClose]);

    return (
        <Modal
            visible={opened}
            onCancel={handleCancel}
            actionText="Save Changes"
            cancelText="Go Back"
            onAction={handleSave}
            isActionButtonPending={loading}
            data-testid="save-changes-modal"
            title={"Confirm Changes"}
        >
            <Control label="The following changes will be saved:">
                <List spacing="xs" size="sm" icon={<CheckIcon />} mb="lg">
                    {changedFields.map((field, index) => (
                        <List.Item key={`${field.field}-${index}`}>{field.label}</List.Item>
                    ))}
                </List>
                {updateError && !loading && <span className={errorClass}>{updateError.message}</span>}
            </Control>
        </Modal>
    );
};
